import { Inject, Injectable } from '@angular/core';
import { deepFreeze } from 'src/util/object';
import { cacheFactory } from 'src/util/cache';
import { PERMISSIONS_CONFIG, PermissionsConfiguration } from './permissions.model';

type PermissionGrants = string[] | 'all';

export interface PermissionCheckResult {
  permission: string;
  granted: boolean;
  permissionSet?: string;
  wildcard: boolean;
  rolePath: string[];
  inherited: boolean;
  description: string;
}

class PermissionCheckHandler {
  private readonly _checkedRoles: string[] = [];
  private readonly _checkedPermissionSets: string[] = [];
  constructor(
    private readonly _config: PermissionsConfiguration,
    private readonly _roles: string[],
    private readonly _permissionToCheck: string,
  ) {}

  private get allUserGrants() {
    return this._config.grants.all;
  }

  private _grant(
    description: string,
    extras?: {
      permissionSet?: string;
      rolePath?: string[];
      all?: boolean;
    },
  ): PermissionCheckResult {
    const rolePath = extras != null && extras.rolePath != null ? extras.rolePath : [];
    return {
      description,
      granted: true,
      permission: this._permissionToCheck,
      permissionSet: extras != null ? extras.permissionSet : undefined,
      rolePath,
      inherited: rolePath.length > 1,
      wildcard: extras != null && extras.all != null ? extras.all : false,
    };
  }

  public hasPermission(): PermissionCheckResult {
    this._checkedRoles.splice(0, this._checkedRoles.length);
    this._checkedPermissionSets.splice(0, this._checkedPermissionSets.length);

    if (this.allUserGrants != null) {
      const permissionsCheck = this._grantedByPermissions(this.allUserGrants.permissions);
      if (permissionsCheck === 'all') {
        return this._grant('All users have all permissions.', { all: true });
      } else if (permissionsCheck === 'included') {
        return this._grant('All users have this permission.');
      }

      const permissionSetOrFalse = this._grantedByPermissionSets(this.allUserGrants.permissionSets);
      if (permissionSetOrFalse !== false) {
        return this._grant(
          `All users have the '${permissionSetOrFalse}' permission set containing the '${this._permissionToCheck}' permission.`,
          {
            permissionSet: permissionSetOrFalse,
          },
        );
      }
    }

    for (const role of this._roles) {
      const roleGrantOrFalse = this._grantedByRole(role, []);
      if (roleGrantOrFalse !== false) {
        return roleGrantOrFalse;
      }
    }

    return {
      description: 'Permission denied',
      granted: false,
      permission: this._permissionToCheck,
      wildcard: false,
      rolePath: [],
      inherited: false,
    };
  }

  private _grantedByPermissionSets(permissionSets: string[] | undefined) {
    if (permissionSets != null) {
      for (const permisssionSet of permissionSets) {
        const permission = this._grantedByPermissionSet(permisssionSet);
        if (permission === 'all' || permission === 'included') {
          return permisssionSet;
        }
      }
    }
    return false;
  }

  private _grantedByPermissionSet(permissionSet: string) {
    if (!this._permissionSetHasBeenChecked(permissionSet)) {
      const permissions = this._getPermissionsForPermissionSet(permissionSet);
      return this._grantedByPermissions(permissions);
    }
    return false;
  }

  private _permissionSetHasBeenChecked(permissionSet: string) {
    return this._checkedPermissionSets.includes(permissionSet);
  }
  private _roleHasBeenChecked(role: string) {
    return this._checkedRoles.includes(role);
  }

  private _markRoleAsChecked(role: string) {
    this._checkedRoles.push(role);
  }
  private _grantedByPermissions(
    permissions: PermissionGrants | undefined,
  ): 'all' | 'included' | false {
    if (permissions != null) {
      if (permissions === 'all') {
        return 'all';
      }
      if (permissions.includes(this._permissionToCheck)) {
        return 'included';
      }
    }
    return false;
  }

  private _grantedByRole(role: string, rolePath: string[]): PermissionCheckResult | false {
    rolePath = [...rolePath, role];
    const inherited = rolePath.length > 1;
    const inheritedStr = inherited ? `(Inherited: ${rolePath.join(' > ')})` : '';
    if (!this._roleHasBeenChecked(role)) {
      const permissions = this._getPermissionsForRole(role);
      const rolePermissionCheck = this._grantedByPermissions(permissions);
      if (rolePermissionCheck === 'all') {
        return this._grant(
          `The '${role}' role is granted all permissions. ${inheritedStr}`.trim(),
          {
            rolePath,
            all: true,
          },
        );
      } else if (rolePermissionCheck === 'included') {
        return this._grant(`Granted by the '${role}' role. ${inheritedStr}`.trim(), {
          rolePath,
        });
      }

      const permissionSets = this._getPermissionSetsForRole(role);
      const permissionSetOrFalse = this._grantedByPermissionSets(permissionSets);
      if (permissionSetOrFalse !== false) {
        return this._grant(
          `Granted by the '${role}' role via the '${permissionSetOrFalse}' permission set. ${inheritedStr}`.trim(),
          {
            rolePath,
            permissionSet: permissionSetOrFalse,
          },
        );
      }

      this._markRoleAsChecked(role);

      const inheritedRoles = this._getInheritedRoles(role);
      for (const inheritedRole of inheritedRoles) {
        const roleGrantOrFalse = this._grantedByRole(inheritedRole, rolePath);
        if (roleGrantOrFalse !== false) {
          return roleGrantOrFalse;
        }
      }
    }
    return false;
  }

  private _getPermissionsForRole(role: string): PermissionGrants | undefined {
    if (this._config.grants.roles != null) {
      const roleCfg = this._config.grants.roles[role];
      return roleCfg != null ? roleCfg.permissions : [];
    }
    return [];
  }

  private _getPermissionSetsForRole(role: string): string[] | undefined {
    if (this._config.grants.roles != null) {
      const roleCfg = this._config.grants.roles[role];
      return roleCfg != null ? roleCfg.permissionSets : [];
    }
    return [];
  }

  private _getInheritedRoles(role: string): string[] {
    if (this._config.grants.roles != null) {
      const roleCfg = this._config.grants.roles[role];
      const inheritedRoles = roleCfg != null ? roleCfg.inherits : [];
      return inheritedRoles != null ? inheritedRoles : [];
    }
    return [];
  }

  private _getPermissionsForPermissionSet(permissionSet: string): string[] {
    if (this._config.permissionSets != null) {
      const permissions = this._config.permissionSets[permissionSet];
      return permissions;
    }
    return [];
  }
}

interface PermissionConfigurationInfo {
  permissionSets: {
    name: string;
    permissions: string[];
  }[];
  permissions: {
    name: string;
    permissionSets: string[];
  }[];
}

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  public readonly config: PermissionConfigurationInfo;

  private _permissionCheckHandlerCache = cacheFactory.keyValueCache<
    { roles: string[]; permission: string },
    PermissionCheckHandler
  >(1000 * 60 * 90);

  constructor(
    // TODO: consider freezing object
    @Inject(PERMISSIONS_CONFIG)
    private readonly _config: PermissionsConfiguration,
  ) {
    const permissionSets =
      _config.permissionSets != null
        ? Object.entries(_config.permissionSets).map(([name, permissions]) => ({
            name,
            permissions,
          }))
        : [];

    const allPermissions: {
      name: string;
      permissionSets: string[];
    }[] = _config.permissions.map((permission) => {
      const includedInSets: string[] = permissionSets
        .filter((set) => set.permissions.includes(permission))
        .map((s) => s.name);
      return { name: permission, permissionSets: includedInSets };
    });

    const cfgInfo: PermissionConfigurationInfo = {
      permissionSets,
      permissions: allPermissions,
    };

    this.config = deepFreeze(cfgInfo);
  }

  public hasPermission(roles: string[], permission: string): boolean {
    return this.getPermissionInfo(roles, permission).granted;
  }

  public hasAnyPermission(roles: string[], permissions: string[]): boolean {
    for (const perm of permissions) {
      if (this.hasPermission(roles, perm)) {
        return true;
      }
    }
    return false;
  }

  public hasAllPermissions(roles: string[], permissions: string[]): boolean {
    for (const perm of permissions) {
      if (!this.hasPermission(roles, perm)) {
        return false;
      }
    }
    return true;
  }

  public getPermissionInfo(roles: string[], permission: string): PermissionCheckResult {
    const key = { roles, permission };
    let handler = this._permissionCheckHandlerCache.get(key);
    if (handler == null) {
      handler = new PermissionCheckHandler(this._config, roles, permission);
      this._permissionCheckHandlerCache.set(key, handler);
    }
    const result = handler.hasPermission();
    return result;
  }
}
